import { CommonModule, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { EditorModule } from '@tinymce/tinymce-angular';
import { ProfilAddressInputComponent } from '../profil/components/profil-address-input/profil-address-input.component';
import { KontaktoplysningerComponent } from '../profil/pages/kontaktoplysninger/kontaktoplysninger.component';
import { ConfirmChangesComponent } from '../projekter/components/confirmation/confirm-changes.component';
import { SagInitiativpligtComponent } from '../projekter/components/sag-initiativpligt/sag-initiativpligt.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { AddressInputComponent } from './components/address-input/address-input.component';
import { AlertComponent } from './components/alert/alert.component';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { BadgeComponent } from './components/badge/badge.component';
import { BaseMapComponent } from './components/basemap/basemap.component';
import { ButtonComponent } from './components/button/button.component';
import { CookieBannerComponent } from './components/cookie-banner/cookie-banner.component';
import { DawaAdresseFormInputComponent } from './components/dawa-adresse-form-input/dawa-adresse-form-input.component';
import { DefaultContactComponent } from './components/default-contact/default-contact.component';
import { DownloadFileComponent } from './components/download-file/download-file.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { EjendomsoegningComponent } from './components/ejendomsoegning/ejendomsoegning.component';
import { ExpandableListItemComponent } from './components/expandable-list-item/expandable-list-item.component';
import { FixedMenuComponent } from './components/fixed-menu/fixed-menu.component';
import { FullPageComponent } from './components/full-page/full-page.component';
import { GdprModalComponent } from './components/gdpr-modal/gdpr-modal.component';
import { InfoIconComponent } from './components/info-icon/info-icon/info-icon.component';
import { HierarchyItemComponent } from './components/items-hierarchy/hierarchy-item/hierarchy-item.component';
import { ItemsHierarchyComponent } from './components/items-hierarchy/items-hierarchy.component';
import { KonfliktMapComponent } from './components/konflikt-map/konflikt-map.component';
import { LinkToPublicwebComponent } from './components/link-to-publicweb/link-to-publicweb.component';
import { LinkComponent } from './components/link/link.component';
import { MatrikelsoegningComponent } from './components/matrikelsoegning/matrikelsoegning.component';
import { ModalComponent } from './components/modal/modal.component';
import { OperatingStatusComponent } from './components/operating-status/operating-status.component';
import { PaginationSelectorComponent } from './components/pagination-selector/pagination-selector.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { SaveIndicatorComponent } from './components/save-indicator/save-indicator/save-indicator.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { StaticMenuComponent } from './components/static-menu/static-menu.component';
import { StedMapComponent } from './components/sted-map/sted-map.component';
import { TimeoutComponent } from './components/timeout/timeout.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { ValidationSummaryComponent } from './components/validation-summary/validation-summary.component';
import { VideoComponent } from './components/video/video.component';
import { VirksomhedsoegningComponent } from './components/virksomhedsoegning/virksomhedsoegning.component';
import { WorkspaceComponent } from './components/workspace/workspace.component';
import { AutofocusableDirective } from './directives/autofocusable.directive';
import { CustomValidationMessageDirective } from './directives/customValidationMessage.directive';
import { ExternalLinkDirective } from './directives/external-link.directive';
import { PopoverDirective } from './directives/popover.directive';
import { TooltipDirective } from './directives/tooltip.directive';
import { BomErrorHandler } from './error-handling/bom-error-handler';
import { CorrelationIdHttpInterceptor } from './error-handling/correlation-id-http-interceptor';
import { ErrorComponent } from './error-handling/error/error.component';
import { AuthenticationHttpInterceptor } from './interceptors/authentication-http-interceptor';
import { FuldmagtHttpInterceptor } from './interceptors/fuldmagt-http-interceptor';
import { NotificationComponent } from './notifications/notification/notification.component';
import { KvitteringRedirectComponent } from './pages/kvittering-redirect/kvittering-redirect.component';
import { MobilePageComponent } from './pages/mobile-page/mobile-page.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { DisableOnRettighedPipe } from './pipes/disable-on-rettighed.pipe';
import { DisplayDatePipe } from './pipes/display-date.pipe';
import { ExternalizeLinksPipe } from './pipes/externalize-links.pipe';
import { FilterBrugerPipe } from './pipes/filter-brugerid.pipe';
import { PostAdressePipe } from './pipes/post-adresse.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { TekstResourcePipe } from './pipes/tekstresource.pipe';
import { WordbreakPipe } from './pipes/wordbreak.pipe';
import { SvgDefinitionsComponent } from './styling/svg-definitions/svg-definitions.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    ReactiveFormsModule,
    RouterModule,
    EditorModule,
  ],
  declarations: [
    AccordionComponent,
    AddressInputComponent,
    AlertComponent,
    AutocompleteComponent,
    AutofocusableDirective,
    BadgeComponent,
    BaseMapComponent,
    ButtonComponent,
    ConfirmChangesComponent,
    CookieBannerComponent,
    CustomValidationMessageDirective,
    DawaAdresseFormInputComponent,
    DefaultContactComponent,
    DisableOnRettighedPipe,
    DisplayDatePipe,
    DownloadFileComponent,
    DropdownMenuComponent,
    EjendomsoegningComponent,
    ErrorComponent,
    ExpandableListItemComponent,
    ExternalizeLinksPipe,
    ExternalLinkDirective,
    FilterBrugerPipe,
    FixedMenuComponent,
    FullPageComponent,
    GdprModalComponent,
    HierarchyItemComponent,
    InfoIconComponent,
    ItemsHierarchyComponent,
    KonfliktMapComponent,
    KontaktoplysningerComponent,
    KvitteringRedirectComponent,
    LinkComponent,
    LinkToPublicwebComponent,
    MatrikelsoegningComponent,
    MobilePageComponent,
    ModalComponent,
    NotificationComponent,
    OperatingStatusComponent,
    PageNotFoundComponent,
    PaginationSelectorComponent,
    PopoverDirective,
    PostAdressePipe,
    ProfilAddressInputComponent,
    RichTextEditorComponent,
    SafeHtmlPipe,
    SagInitiativpligtComponent,
    SaveIndicatorComponent,
    SpinnerComponent,
    StaticMenuComponent,
    StedMapComponent,
    SvgDefinitionsComponent,
    TekstResourcePipe,
    TimeoutComponent,
    TooltipComponent,
    TooltipDirective,
    ValidationSummaryComponent,
    VideoComponent,
    VirksomhedsoegningComponent,
    WordbreakPipe,
    WorkspaceComponent,
  ],
  exports: [
    AccordionComponent,
    AddressInputComponent,
    AlertComponent,
    AutocompleteComponent,
    AutofocusableDirective,
    BadgeComponent,
    BaseMapComponent,
    ButtonComponent,
    ConfirmChangesComponent,
    CookieBannerComponent,
    CustomValidationMessageDirective,
    DawaAdresseFormInputComponent,
    DefaultContactComponent,
    DisableOnRettighedPipe,
    DisplayDatePipe,
    DownloadFileComponent,
    DropdownMenuComponent,
    EjendomsoegningComponent,
    ErrorComponent,
    ExpandableListItemComponent,
    ExternalizeLinksPipe,
    ExternalLinkDirective,
    FilterBrugerPipe,
    FixedMenuComponent,
    FullPageComponent,
    GdprModalComponent,
    HierarchyItemComponent,
    InfoIconComponent,
    ItemsHierarchyComponent,
    KonfliktMapComponent,
    KontaktoplysningerComponent,
    KvitteringRedirectComponent,
    LinkComponent,
    LinkToPublicwebComponent,
    MatrikelsoegningComponent,
    MobilePageComponent,
    ModalComponent,
    NotificationComponent,
    OperatingStatusComponent,
    PageNotFoundComponent,
    PaginationSelectorComponent,
    PopoverDirective,
    PostAdressePipe,
    RichTextEditorComponent,
    SafeHtmlPipe,
    SagInitiativpligtComponent,
    SaveIndicatorComponent,
    SpinnerComponent,
    StaticMenuComponent,
    StedMapComponent,
    SvgDefinitionsComponent,
    TekstResourcePipe,
    TimeoutComponent,
    TooltipDirective,
    VideoComponent,
    VirksomhedsoegningComponent,
    WordbreakPipe,
    WorkspaceComponent,
  ],
  providers: [
    DecimalPipe,
    DisplayDatePipe,
    FilterBrugerPipe,
    TekstResourcePipe,
    PostAdressePipe,
    {
      provide: ErrorHandler,
      useClass: BomErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationIdHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FuldmagtHttpInterceptor,
      multi: true,
    },
  ],
  entryComponents: [TooltipComponent],
})
export class CoreModule {}
